/**
 * PitchDeckReviewRenderer (T-062, ARCH-005 §1/§9, TECH-09, AI-02).
 *
 * Renders the Zod-validated pitch_deck_review payload: a summary scorecard
 * (four 1..5 dimensions) and an annotated slide block (thumbnail image when
 * the agent supplied a `thumbnail_url`, plus Eric's note and a severity pill).
 * The thumbnail "upload pipeline" (T-062 AC) is the deliverable content
 * payload itself: the agent/operator stores a public thumbnail_url on each
 * slide (Supabase storage public bucket or external); the renderer surfaces
 * it. No client upload widget in V1 (the public /d/<id> view is read-only,
 * ARCH-002 public-view-only). Body only.
 *
 * Verbosity (ARCH-005 §5): the scorecard + slide notes are the deliverable
 * and always show; 'build_it_with_me' adds the methodology callout.
 */

import {
  MethodologyCallout,
  SectionHead,
  type RendererProps,
} from '../shared';
import type { PitchDeckReviewContent } from './payload';

const SEVERITY_STYLE: Record<
  PitchDeckReviewContent['slides'][number]['severity'],
  string
> = {
  strong:
    'bg-[color:var(--color-linen)]/60 text-[color:var(--color-navy)]',
  okay: 'bg-[color:var(--color-white)] text-[color:var(--color-text-mute)]',
  rework:
    'border border-[color:var(--color-terracotta)] text-[color:var(--color-terracotta)]',
};

export function PitchDeckReviewRenderer({
  payload,
  verbosity,
}: RendererProps<PitchDeckReviewContent>) {
  const s = payload.scorecard;
  return (
    <div className="space-y-6">
      <div>
        <SectionHead>Scorecard</SectionHead>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ['Clarity', s.clarity],
              ['Structure', s.structure],
              ['Differentiation', s.differentiation],
              ['Voice', s.voice],
            ] as const
          ).map(([label, val]) => (
            <div
              key={label}
              className="rounded-md border border-[color:var(--color-rule)] p-3 text-center"
            >
              <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-mute)]">
                {label}
              </p>
              <p className="font-display text-2xl text-[color:var(--color-navy)]">
                {val}/5
              </p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-sm text-[color:var(--color-navy)]">
          {s.overall_summary}
        </p>
      </div>

      <div className="space-y-3">
        <SectionHead>Slide notes</SectionHead>
        {payload.slides.map((sl, i) => (
          <div
            key={i}
            className="flex flex-col gap-3 rounded-md border border-[color:var(--color-rule)] p-3 sm:flex-row"
          >
            {sl.thumbnail_url ? (
              <img
                src={sl.thumbnail_url}
                alt={`Slide ${sl.slide_index + 1} thumbnail`}
                loading="lazy"
                className="h-auto w-full max-w-[200px] flex-none rounded-sm border border-[color:var(--color-rule)] object-cover"
              />
            ) : null}
            <div className="min-w-0">
              <div className="mb-1 flex items-center gap-2">
                <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-mute)]">
                  Slide {sl.slide_index + 1}
                </p>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_STYLE[sl.severity]}`}
                >
                  {sl.severity}
                </span>
              </div>
              <p className="text-sm text-[color:var(--color-navy)]">
                {sl.eric_note}
              </p>
            </div>
          </div>
        ))}
      </div>

      <MethodologyCallout verbosity={verbosity}>
        <p>
          A deck review is not a polish pass. Read for the one slide a sharp
          investor stops on and cannot move past. Fix that slide first. The rest
          is rhythm.
        </p>
      </MethodologyCallout>
    </div>
  );
}
